import { Box, Stack, Typography } from '@mui/material';
import { PartnerBusinessHours } from '@provision/entity-types';
import { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isNullish, prop, sortBy, unique } from 'remeda';
import { Loader, NamedLayoutBlock } from 'src/components';
import { WeekSchedule, WeekScheduleFormFields } from 'src/components/WeekSchedule';
import {
  usePartnerBusinessHours,
  usePartnerBusinessHoursCUDOperations,
} from 'src/graphql/entities/partner-business-hours';
import { ApplyToAllDays } from 'src/pages/Settings/shared/apply-to-all-days';
import { useGlobalStore } from 'src/store';
import { DEFAULT_DATE, TIME_FORMAT } from './const';

const mapBusinessHourToFormValue = (it: Partial<PartnerBusinessHours>) => ({
  id: it.partnerBusinessHoursId || '',
  weekDay: it.weekDay ?? 0,
  createdAt: it.createdAt || '',
  from: `${DEFAULT_DATE}T${it.timeFrom || ''}`,
  to: `${DEFAULT_DATE}T${it.timeTo || ''}`,
});

const normalizeBusinessHours = (businessHours: PartnerBusinessHours[]) => {
  return sortBy(businessHours, [prop('weekDay'), 'asc'], [prop('createdAt'), 'asc']).map(
    mapBusinessHourToFormValue,
  );
};

export const BusinessHours = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const { partnerBusinessHours, loading: isLoadingPartnerBusinessHours } =
    usePartnerBusinessHours({
      partnerId: isNullish(selectedPartnerId) ? undefined : selectedPartnerId,
    });

  const {
    deletePartnerBusinessHours,
    createPartnerBusinessHours,
    createPartnerBusinessHoursWithTimes,
    updatePartnerBusinessHours,
    getBusinessHoursCreateOptimisticResponse,
  } = usePartnerBusinessHoursCUDOperations();

  const partnerBusinessHoursList = partnerBusinessHours as PartnerBusinessHours[];
  const partnerBusinessHoursLength = partnerBusinessHoursList.length;

  const [scheduleKey, setScheduleKey] = useState(0);
  const [showApplyToAll, setShowApplyToAll] = useState(partnerBusinessHoursLength > 0);

  useEffect(() => {
    if (partnerBusinessHoursLength > 0 && !showApplyToAll) {
      setShowApplyToAll(true);
    }
  }, [partnerBusinessHoursLength, showApplyToAll]);

  const idsToDelete = useMemo(
    () => partnerBusinessHoursList.map((it) => it.partnerBusinessHoursId),
    [partnerBusinessHoursList],
  );

  const activeDays = useMemo(
    () => unique(partnerBusinessHoursList.map((it) => it.weekDay)),
    [partnerBusinessHoursList],
  );

  const onApplyToAll = useCallback(
    async (timeWindows: { from: Dayjs; to: Dayjs }[]) => {
      if (isNullish(selectedPartnerId)) return;
      try {
        if (idsToDelete.length) {
          const toDelete = partnerBusinessHoursList.filter((it) =>
            idsToDelete.includes(it.partnerBusinessHoursId),
          );
          await deletePartnerBusinessHours(toDelete);
        }

        const createPromises = activeDays.flatMap((weekDay) =>
          timeWindows.map((tw) =>
            createPartnerBusinessHoursWithTimes({
              partnerId: selectedPartnerId,
              weekDay,
              timeFrom: tw.from.format(TIME_FORMAT),
              timeTo: tw.to.format(TIME_FORMAT),
            }),
          ),
        );
        await Promise.all(createPromises);

        setScheduleKey((prev) => prev + 1);
      } catch (error) {
        console.error('Failed to apply time windows to all days:', error);
      }
    },
    [
      selectedPartnerId,
      idsToDelete,
      activeDays,
      partnerBusinessHoursList,
      deletePartnerBusinessHours,
      createPartnerBusinessHoursWithTimes,
    ],
  );

  const formValues = useMemo(
    () => ({
      [WeekScheduleFormFields.Schedule]: normalizeBusinessHours(partnerBusinessHoursList),
    }),
    [partnerBusinessHoursList],
  );

  const onDeleteTimeWindow = useCallback(
    (ids: string | string[]) => {
      const foundItems = partnerBusinessHoursList.filter((it) =>
        ids.includes(it.partnerBusinessHoursId),
      );
      if (foundItems.length) {
        void deletePartnerBusinessHours(foundItems);
      }
    },
    [deletePartnerBusinessHours, partnerBusinessHoursList],
  );

  const onAddTimeWindow = useCallback(
    (dayIndex: number) => {
      return {
        create: async () => {
          const result = await createPartnerBusinessHours(selectedPartnerId, dayIndex);
          return result?.data?.createPartnerBusinessHours?.[0] as PartnerBusinessHours;
        },
        optimisticResponse: getBusinessHoursCreateOptimisticResponse(
          selectedPartnerId!,
          dayIndex,
        ),
      };
    },
    [createPartnerBusinessHours, getBusinessHoursCreateOptimisticResponse, selectedPartnerId],
  );

  const onTimeChange = useCallback(
    (id: string, inputType: 'from' | 'to', time: Dayjs | null) => {
      if (id && time) {
        const businessHoursObj = partnerBusinessHoursList.find((bh) => bh.id === id);

        const formattedTime = time.format(TIME_FORMAT);
        updatePartnerBusinessHours(id, inputType, formattedTime, businessHoursObj);
      }
    },
    [updatePartnerBusinessHours, partnerBusinessHoursList],
  );

  return (
    <NamedLayoutBlock
      title="Business Hours"
      sx={{
        flex: 1,
        display: { sm: 'flex' },
      }}
    >
      <Stack direction="row" justifyContent="center" padding={5}>
        <Box
          style={{
            width: '35%',
            minWidth: 560,
          }}
        >
          <Typography variant="h5">Business Hours</Typography>
          <Typography variant="body2" mt={1}>
            Set up office hours for your workspace to manage clients expectations and internal
            workflows. Times are based on the partner&apos;s timezone.
          </Typography>
          <Box mt={2}>
            {isLoadingPartnerBusinessHours ? (
              <Loader />
            ) : (
              <>
                {showApplyToAll ? (
                  <ApplyToAllDays
                    onApply={onApplyToAll}
                    hasActiveDays={activeDays.length > 0}
                    maxTimeWindows={3}
                  />
                ) : null}
                <WeekSchedule
                  key={scheduleKey}
                  values={formValues}
                  turnedOffDateLabel="Outside of business hours"
                  maxTimeWindows={3}
                  onDeleteTimeWindow={onDeleteTimeWindow}
                  onAddTimeWindow={onAddTimeWindow}
                  onChangeTimeWindow={onTimeChange}
                  mapEntityToFormValue={mapBusinessHourToFormValue}
                />
              </>
            )}
          </Box>
        </Box>
      </Stack>
    </NamedLayoutBlock>
  );
};
